import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { AuthState, User } from '../types/user';
import authService from '../services/authService';

// Fonction pour initialiser l'état de manière sécurisée
const getInitialState = (): AuthState => {
  // Vérifier si window est défini (côté client)
  if (typeof window !== 'undefined') {
    return {
      user: authService.getCurrentUser(),
      token: localStorage.getItem('token'),
      isAuthenticated: !!localStorage.getItem('user'),
      isLoading: false,
      error: null,
    };
  }
  
  // État par défaut pour le rendu côté serveur
  return {
    user: null,
    token: null,
    isAuthenticated: false,
    isLoading: false,
    error: null,
  };
};

// État initial
const initialState: AuthState = getInitialState();

// Actions asynchrones
export const login = createAsyncThunk(
  'auth/login',
  async ({ email, password }: { email: string; password: string }, { rejectWithValue }) => {
    try {
      const result = await authService.login(email, password);
      if (result) {
        localStorage.setItem('user', JSON.stringify(result.user));
        localStorage.setItem('token', result.token);
        return { user: result.user, token: result.token };
      }
      return rejectWithValue('Identifiants invalides');
    } catch (error) {
      return rejectWithValue('Erreur de connexion');
    }
  }
);

// Login via Google
export const loginWithGoogle = createAsyncThunk(
  'auth/loginWithGoogle',
  async (_, { rejectWithValue }) => {
    try {
      const user = await authService.loginWithGoogle();
      if (user) {
        localStorage.setItem('user', JSON.stringify(user));
        localStorage.setItem('token', 'fake-jwt-token');
        return { user, token: 'fake-jwt-token' };
      }
      return rejectWithValue('Connexion Google échouée');
    } catch (error) {
      return rejectWithValue('Erreur de connexion Google');
    }
  }
);

export const logout = createAsyncThunk<void, void>('auth/logout', async () => {
  authService.logout();
});

export const getAllUsers = createAsyncThunk('auth/getAllUsers', async (_, { rejectWithValue }) => {
  try {
    return await authService.getAllUsers();
  } catch (error) {
    return rejectWithValue('Erreur lors de la récupération des utilisateurs');
  }
});

export const createUser = createAsyncThunk(
  'auth/createUser',
  async (userData: Omit<User, 'id' | 'createdAt' | 'updatedAt'>, { rejectWithValue }) => {
    try {
      return await authService.createUser(userData);
    } catch (error) {
      return rejectWithValue('Erreur lors de la création de l\'utilisateur');
    }
  }
);

export const updateUser = createAsyncThunk(
  'auth/updateUser',
  async ({ id, userData }: { id: string; userData: Partial<User> }, { rejectWithValue }) => {
    try {
      const updatedUser = await authService.updateUser(id, userData);
      if (!updatedUser) {
        return rejectWithValue('Utilisateur non trouvé');
      }
      return updatedUser;
    } catch (error) {
      return rejectWithValue('Erreur lors de la mise à jour de l\'utilisateur');
    }
  }
);

export const deleteUser = createAsyncThunk(
  'auth/deleteUser',
  async (id: string, { rejectWithValue }) => {
    try {
      const success = await authService.deleteUser(id);
      if (!success) {
        return rejectWithValue('Utilisateur non trouvé');
      }
      return id;
    } catch (error) {
      return rejectWithValue('Erreur lors de la suppression de l\'utilisateur');
    }
  }
);

export const changeUserStatus = createAsyncThunk(
  'auth/changeUserStatus',
  async ({ id, status }: { id: string; status: 'active' | 'suspended' }, { rejectWithValue }) => {
    try {
      const updatedUser = await authService.changeUserStatus(id, status);
      if (!updatedUser) {
        return rejectWithValue('Utilisateur non trouvé');
      }
      return updatedUser;
    } catch (error) {
      return rejectWithValue('Erreur lors du changement de statut de l\'utilisateur');
    }
  }
);

// Slice
const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    // Permet de définir l'utilisateur après un flux signInWithRedirect
    setAuthenticated: (state, action: PayloadAction<{ user: User; token: string }>) => {
      state.isLoading = false;
      state.isAuthenticated = true;
      state.user = action.payload.user;
      state.token = action.payload.token;
    },
  },
  extraReducers: (builder) => {
    builder
      // Login
      .addCase(login.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(login.fulfilled, (state, action: PayloadAction<{ user: User; token: string }>) => {
        state.isLoading = false;
        state.isAuthenticated = true;
        state.user = action.payload.user;
        state.token = action.payload.token;
      })
      .addCase(login.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      })

      // Login with Google
      .addCase(loginWithGoogle.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(loginWithGoogle.fulfilled, (state, action: PayloadAction<{ user: User; token: string }>) => {
        state.isLoading = false;
        state.isAuthenticated = true;
        state.user = action.payload.user;
        state.token = action.payload.token;
      })
      .addCase(loginWithGoogle.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      })
      
      // Logout
      .addCase(logout.fulfilled, (state) => {
        state.user = null;
        state.token = null;
        state.isAuthenticated = false;
      })
      
      // Get All Users
      .addCase(getAllUsers.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(getAllUsers.fulfilled, (state) => {
        state.isLoading = false;
      })
      .addCase(getAllUsers.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      })
      
      // Create User
      .addCase(createUser.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(createUser.fulfilled, (state) => {
        state.isLoading = false;
      })
      .addCase(createUser.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      })
      
      // Update User
      .addCase(updateUser.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(updateUser.fulfilled, (state) => {
        state.isLoading = false;
      })
      .addCase(updateUser.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      })
      
      // Delete User
      .addCase(deleteUser.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(deleteUser.fulfilled, (state) => {
        state.isLoading = false;
      })
      .addCase(deleteUser.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      })
      
      // Change User Status
      .addCase(changeUserStatus.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(changeUserStatus.fulfilled, (state) => {
        state.isLoading = false;
      })
      .addCase(changeUserStatus.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      });
  },
});

export const { clearError, setAuthenticated } = authSlice.actions;
export default authSlice.reducer;
